// Admin Panel Utility Functions

/**
 * Export data to CSV format
 */
export const exportToCSV = (data, filename = 'export.csv') => {
    if (!data || data.length === 0) {
        alert('No data to export');
        return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
        headers.join(','),
        ...data.map(row =>
            headers.map(header => {
                const value = row[header];
                // Handle arrays (like requirements)
                if (Array.isArray(value)) {
                    return `"${value.join('; ')}"`;
                }
                // Escape quotes and wrap in quotes if contains comma
                const stringValue = String(value || '');
                return stringValue.includes(',') || stringValue.includes('"')
                    ? `"${stringValue.replace(/"/g, '""')}"`
                    : stringValue;
            }).join(',')
        )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Export data to JSON format
 */
export const exportToJSON = (data, filename = 'export.json') => {
    if (!data || data.length === 0) {
        alert('No data to export');
        return;
    }

    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Filter data by search query
 */
export const filterBySearch = (data, searchQuery, searchFields = []) => {
    if (!searchQuery) return data;

    const query = searchQuery.toLowerCase().trim();
    return data.filter(item => {
        // If no specific fields provided, search all string fields
        const fieldsToSearch = searchFields.length > 0
            ? searchFields
            : Object.keys(item).filter(key => typeof item[key] === 'string');

        return fieldsToSearch.some(field => {
            const value = item[field];
            if (typeof value === 'string') {
                return value.toLowerCase().includes(query);
            }
            if (Array.isArray(value)) {
                return value.some(v => String(v).toLowerCase().includes(query));
            }
            return false;
        });
    });
};

/**
 * Filter data by status
 */
export const filterByStatus = (data, status) => {
    if (!status || status === 'all') return data;
    return data.filter(item => item.status === status);
};

/**
 * Filter data by date range
 */
export const filterByDateRange = (data, startDate, endDate) => {
    if (!startDate && !endDate) return data;

    return data.filter(item => {
        if (!item.date) return false;
        const itemDate = new Date(item.date);

        if (startDate && endDate) {
            return itemDate >= new Date(startDate) && itemDate <= new Date(endDate);
        } else if (startDate) {
            return itemDate >= new Date(startDate);
        } else {
            return itemDate <= new Date(endDate);
        }
    });
};

/**
 * Sort data by field
 */
export const sortData = (data, field, order = 'asc') => {
    return [...data].sort((a, b) => {
        const aVal = a[field];
        const bVal = b[field];

        // Handle dates
        if (field === 'date' || field === 'createdAt') {
            const aDate = new Date(aVal);
            const bDate = new Date(bVal);
            return order === 'asc' ? aDate - bDate : bDate - aDate;
        }

        // Handle numbers
        if (typeof aVal === 'number' && typeof bVal === 'number') {
            return order === 'asc' ? aVal - bVal : bVal - aVal;
        }

        // Handle strings
        const aStr = String(aVal || '').toLowerCase();
        const bStr = String(bVal || '').toLowerCase();

        if (order === 'asc') {
            return aStr.localeCompare(bStr);
        } else {
            return bStr.localeCompare(aStr);
        }
    });
};

/**
 * Paginate data
 */
export const paginateData = (data, page = 1, pageSize = 10) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return {
        data: data.slice(startIndex, endIndex),
        totalPages: Math.ceil(data.length / pageSize),
        currentPage: page,
        totalItems: data.length
    };
};

/**
 * Format date for display
 */
export const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
};

/**
 * Format date and time for display
 */
export const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    return date.toLocaleDateString('en-US', options);
};

/**
 * Get statistics from data
 */
export const getStatistics = (data, groupBy = 'status') => {
    if (!data || data.length === 0) return {};

    return data.reduce((acc, item) => {
        const key = item[groupBy] || 'Unknown';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});
};

/**
 * Calculate trend (percentage change)
 */
export const calculateTrend = (currentValue, previousValue) => {
    if (!previousValue) return 0;
    return ((currentValue - previousValue) / previousValue) * 100;
};

/**
 * Debounce function for search inputs
 */
export const debounce = (func, wait = 300) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};
